import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { t } from 'ember-intl'
import type { TOC } from '@ember/component/template-only'
import { parseISO } from 'date-fns'
import Disappointed from '@images/emoji-disappointed.svg'
import Smile from '@images/emoji-smile.svg'
import Unsure from '@images/emoji-unsure.svg'
import { join } from 'teacher-ui-client/utils/uri'
import { eq } from 'ember-truth-helpers'

const defaultDateFormat = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

const truncateByWords = (text: string, wordLimit: number, suffix = '...') => {
  const wordList = text.split(' ')
  if (wordList.length > wordLimit) return wordList.slice(0, wordLimit).join(' ') + suffix
  return text
}

const ReflectionImages: { [key: string]: typeof Smile } = {
  smile: Smile,
  unsure: Unsure,
  disappointed: Disappointed,
}

interface RecentWritingStatsItemSignature {
  Args: {
    name?: string
  }
  Blocks: {
    default: []
  }
}

interface RecentWritingCardData {
  type?: string
  studentId: string
  firstName: string
  lastName: string
  avatar?: string
  className?: string
  activityId: string
  activityTitle: string
  latestResultSnapshotId: string
  snapshotUpdatedDate: string
  answerPlainText?: string
  statsWordCount?: number
  statsAutoFeedbackScore?: number
  reflection?: string
  activityFeedbackId?: string
  userTargetWordCount?: number
}

interface RecentWritingCardSignature {
  Element: HTMLLIElement
  Args: RecentWritingCardData
}

const getAvatarFullPath = (avatar = 'avatar-001') => {
  return `https://content-assets.static.readingeggs.com/writing-legends/activities/common/images/avatars/${avatar}.png`
}

const RecentWritingStatsItem: TOC<RecentWritingStatsItemSignature> = <template>
  <div class="flex flex-col lg:flex-row"><div
      class="text-dusty-black-200 text-center uppercase lg:text-left"
    >{{@name}}</div><div class="ml-1 text-center lg:text-left">{{yield}}</div></div>
</template>

export class RecentWritingCardEWIAndLongWrite extends Component<RecentWritingCardSignature> {
  get maxInstantLegendsFeedbackScore() {
    // Long Write activities will have a user target word count
    // EWI activities will default to 100
    return this.args.userTargetWordCount ?? 100
  }
  get avatarFullPath() {
    return getAvatarFullPath(this.args.avatar)
  }

  get reflectionEmoji() {
    if (!this.args.reflection) return null
    try {
      const reflectionObject = JSON.parse(this.args.reflection) as { emoji: 'smile' | 'unsure' | 'disappointed' }
      return ReflectionImages[reflectionObject.emoji]
    } catch {
      return null
    }
  }

  get answerPlainTextTruncated() {
    if (!this.args.answerPlainText) return ''
    return truncateByWords(this.args.answerPlainText, 50)
  }

  get lastUpdatedDate() {
    return defaultDateFormat.format(parseISO(this.args.snapshotUpdatedDate))
  }

  get viewLink() {
    const linkType = this.args.type === 'long-write' ? 'long-writes' : 'writing-ideas'

    return join(
      `/writing/${linkType}/submission`,
      this.args.latestResultSnapshotId,
      'activity',
      this.args.activityId,
      'student',
      this.args.studentId,
    ).href
  }

  <template>
    <li class="flex min-h-24 border-2 p-2 shadow-lg" ...attributes>
      <div class="basis-80 text-center">
        <img
          width="60"
          class="mx-auto"
          alt={{t "pages.recentWriting.card.studentAvatarAlt"}}
          src={{this.avatarFullPath}}
        />
        <div class="font-bold">{{@firstName}} {{@lastName}}</div>
        <div>{{@className}}</div>
      </div>
      <div class="basis-full">
        <div class="flex justify-between border-b p-1">
          <RecentWritingStatsItem @name="Words">{{@statsWordCount}}</RecentWritingStatsItem>
          <RecentWritingStatsItem @name="When">{{this.lastUpdatedDate}}</RecentWritingStatsItem>
          <RecentWritingStatsItem @name="Feedback Given">
            {{#if @activityFeedbackId}}
              <PhosphorIcon @icon="phosphor-check-bold" />
            {{else}}
              &ndash;
            {{/if}}
          </RecentWritingStatsItem>
          <RecentWritingStatsItem @name="Student Reflection">
            {{#if @reflection}}
              <div class="flex justify-center"><img
                  src={{this.reflectionEmoji}}
                  alt={{t "pages.recentWriting.card.studentReflectionAlt"}}
                  class="w-8"
                /></div>
            {{/if}}
          </RecentWritingStatsItem>
          <RecentWritingStatsItem
            @name="Instant Legend Feedback"
          >{{@statsAutoFeedbackScore}}/{{this.maxInstantLegendsFeedbackScore}}</RecentWritingStatsItem>
        </div>
        <div class="flex">
          <div>
            <div class="flex h-full w-36 flex-col">
              {{#if (eq @type "long-write")}}
                <div
                  class="bg-orangy-pink-100 text-deep-orange-350 mt-1 w-fit flex-none rounded-full px-4 text-sm uppercase"
                >{{t "pages.longWrites.activityTypes.persuasive"}}</div>
              {{/if}}
              <div class="flex grow items-center font-bold">
                {{@activityTitle}}
              </div>
            </div>
          </div>
          <div class="mx-8 my-4 grow">{{this.answerPlainTextTruncated}}</div>
          <a data-test-view-link class="my-4 flex-none text-center font-bold" href={{this.viewLink}}>
            <div><PhosphorIcon @icon="phosphor-file-text-fill" class="h-8 w-8" /></div>
            <div>{{t "pages.recentWriting.card.viewLink"}}</div>
          </a>
        </div>
      </div>
    </li>
  </template>
}

export default RecentWritingCardEWIAndLongWrite
